import { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import {
  collection, onSnapshot, query, orderBy, where,
  addDoc, updateDoc, doc, serverTimestamp, getDocs
} from "firebase/firestore";
import jsPDF from "jspdf";

const CASE_STATUSES = [
  { id: "open",            label: "Open",              color: "#2E7D52", bg: "#F0FFF4" },
  { id: "investigating",   label: "Under Investigation",color: "#C9A84C", bg: "#FFF8E7" },
  { id: "escalated",       label: "Escalated",         color: "#C0392B", bg: "#FEF2F2" },
  { id: "closed",          label: "Closed",            color: "#9B8F7A", bg: "#F4F2EE" },
];

const S = {
  card: { background:"#FFFFFF", border:"1px solid #E2DDD6", borderRadius:10, padding:16, marginBottom:12 },
  inp:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"9px 12px", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" },
  sel:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"9px 12px", borderRadius:7, fontSize:13, width:"100%" },
  btn:  (bg,col,brd) => ({ background:bg, color:col||"#fff", border:`1px solid ${brd||bg}`, padding:"8px 18px", borderRadius:7, cursor:"pointer", fontSize:12, fontWeight:700 }),
  lbl:  { fontSize:10, color:"#9B8F7A", letterSpacing:1.5, display:"block", marginBottom:5, textTransform:"uppercase", fontWeight:700 },
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
  } catch { return "—"; }
};

const genCaseNumber = () => {
  const yr = new Date().getFullYear();
  const rand = Math.floor(Math.random() * 900) + 100;
  return `CS-${yr}-${rand}`;
};

export default function CaseManagement({ focusCase, onFocusHandled }) {
  const [cases, setCases]         = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [officers, setOfficers]   = useState([]);
  const [selected, setSelected]   = useState(null);

  // Deep-link from global search: open the case detail for the passed object
  // (uses the passed object directly, so it works regardless of this page's own
  // tenant-scoped load — see the Synapse backlog re: auth.currentUser.tenantId).
  useEffect(() => {
    if (!focusCase) return;
    setSelected(focusCase);
    onFocusHandled && onFocusHandled();
  }, [focusCase]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showNew, setShowNew]     = useState(false);
  const [filter, setFilter]       = useState("all");
  const [newNote, setNewNote]     = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [form, setForm] = useState({
    title:"", description:"", assigned_to:"", priority:"medium", linked_incidents:[]
  });

  const tenantId = auth.currentUser?.tenantId || "osecurity-ltd";

  useEffect(() => {
    const q = query(collection(db,"cases"), where("tenant_id","==",tenantId), orderBy("created_at","desc"));
    return onSnapshot(q, snap => setCases(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [tenantId]);

  useEffect(() => {
    const q = query(collection(db,"incidents"), where("companyId","==",tenantId), orderBy("createdAt","desc"), );
    return onSnapshot(q, snap => setIncidents(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [tenantId]);

  useEffect(() => {
    const q = query(collection(db,"users"), where("tenant_id","==",tenantId));
    return onSnapshot(q, snap => setOfficers(snap.docs.map(d => ({ uid:d.id, ...d.data() }))));
  }, [tenantId]);

  const createCase = async () => {
    if (!form.title) { alert("Case title required"); return; }
    const ref = await addDoc(collection(db,"cases"), {
      case_number:       genCaseNumber(),
      title:             form.title,
      description:       form.description,
      status:            "open",
      priority:          form.priority,
      assigned_to:       form.assigned_to,
      assigned_name:     officers.find(o=>o.uid===form.assigned_to)?.name || form.assigned_to,
      linked_incidents:  form.linked_incidents,
      evidence:          [],
      notes:             [],
      tenant_id:         tenantId,
      created_by:        auth.currentUser?.email || "admin",
      created_at:        serverTimestamp(),
      updated_at:        serverTimestamp(),
    });
    setShowNew(false);
    setForm({ title:"", description:"", assigned_to:"", priority:"medium", linked_incidents:[] });
    // Open the new case
    const snap = await getDocs(query(collection(db,"cases"), where("tenant_id","==",tenantId)));
    const newCase = snap.docs.find(d=>d.id===ref.id);
    if (newCase) setSelected({ id:newCase.id, ...newCase.data() });
  };

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db,"cases",id), { status, updated_at:serverTimestamp() });
    if (selected?.id === id) setSelected(s => ({ ...s, status }));
  };

  const addNote = async () => {
    if (!newNote.trim() || !selected) return;
    setAddingNote(true);
    const note = {
      text:       newNote.trim(),
      author:     auth.currentUser?.email || "admin",
      created_at: new Date().toISOString(),
    };
    const updatedNotes = [...(selected.notes||[]), note];
    await updateDoc(doc(db,"cases",selected.id), { notes:updatedNotes, updated_at:serverTimestamp() });
    setSelected(s => ({ ...s, notes:updatedNotes }));
    setNewNote("");
    setAddingNote(false);
  };

  const exportPDF = async (c) => {
    const pdf = new jsPDF();
    const pg  = pdf.internal.pageSize;

    // Header
    pdf.setFillColor(26,26,46);
    pdf.rect(0,0,pg.getWidth(),40,"F");
    pdf.setTextColor(201,168,76);
    pdf.setFontSize(18); pdf.setFont("helvetica","bold");
    pdf.text("oSecurity Ltd",14,18);
    pdf.setFontSize(11); pdf.setTextColor(200,200,200);
    pdf.text("Case Investigation Report",14,28);
    pdf.setTextColor(150,150,150);
    pdf.text(`Generated: ${new Date().toLocaleDateString("en-GB",{weekday:"long",day:"2-digit",month:"long",year:"numeric"})}`,14,36);

    let y = 52;

    // Case details
    pdf.setTextColor(26,26,46);
    pdf.setFontSize(14); pdf.setFont("helvetica","bold");
    pdf.text(`Case ${c.case_number}: ${c.title}`,14,y); y+=10;

    const sc = CASE_STATUSES.find(s=>s.id===c.status)||CASE_STATUSES[0];
    pdf.setFontSize(10); pdf.setFont("helvetica","normal");
    pdf.setTextColor(100,100,100);
    pdf.text(`Status: ${sc.label}  |  Priority: ${(c.priority||"medium").toUpperCase()}  |  Assigned: ${c.assigned_name||"Unassigned"}`,14,y); y+=8;
    pdf.text(`Created: ${c.created_at?.toDate ? c.created_at.toDate().toLocaleDateString("en-GB") : "—"}`,14,y); y+=12;

    // Description
    if (c.description) {
      pdf.setFontSize(11); pdf.setFont("helvetica","bold"); pdf.setTextColor(26,26,46);
      pdf.text("Description",14,y); y+=6;
      pdf.setFont("helvetica","normal"); pdf.setFontSize(10); pdf.setTextColor(80,80,80);
      const desc = pdf.splitTextToSize(c.description, pg.getWidth()-28);
      pdf.text(desc,14,y); y += desc.length*5+8;
    }

    // Linked incidents
    const linked = incidents.filter(i => (c.linked_incidents||[]).includes(i.id));
    if (linked.length) {
      pdf.setFontSize(11); pdf.setFont("helvetica","bold"); pdf.setTextColor(26,26,46);
      pdf.text(`Linked Incidents (${linked.length})`,14,y); y+=8;
      linked.forEach((inc,idx) => {
        if (y > pg.getHeight()-30) { pdf.addPage(); y=20; }
        pdf.setFillColor(248,247,244);
        pdf.roundedRect(14,y-4,pg.getWidth()-28,16,2,2,"F");
        pdf.setFont("helvetica","bold"); pdf.setFontSize(10); pdf.setTextColor(26,26,46);
        pdf.text(`${idx+1}. ${inc.category||"Incident"} — ${inc.siteName||"Unknown site"}`,18,y+4);
        pdf.setFont("helvetica","normal"); pdf.setTextColor(100,100,100);
        pdf.text(`Level: ${inc.escalationLevel||"—"}  |  Officer: ${inc.guardName||"—"}  |  ${inc.createdAt?.toDate?inc.createdAt.toDate().toLocaleDateString("en-GB"):"—"}`,18,y+10);
        y+=20;
      });
      y+=4;
    }

    // Notes / Evidence
    if ((c.notes||[]).length) {
      if (y > pg.getHeight()-40) { pdf.addPage(); y=20; }
      pdf.setFontSize(11); pdf.setFont("helvetica","bold"); pdf.setTextColor(26,26,46);
      pdf.text("Investigation Notes",14,y); y+=8;
      c.notes.forEach((note,idx) => {
        if (y > pg.getHeight()-30) { pdf.addPage(); y=20; }
        pdf.setFont("helvetica","bold"); pdf.setFontSize(9); pdf.setTextColor(100,100,100);
        pdf.text(`${note.author} — ${new Date(note.created_at).toLocaleDateString("en-GB")}`,14,y); y+=5;
        pdf.setFont("helvetica","normal"); pdf.setTextColor(50,50,50);
        const noteLines = pdf.splitTextToSize(note.text, pg.getWidth()-28);
        pdf.text(noteLines,14,y); y+=noteLines.length*4+6;
      });
    }

    // Footer
    pdf.setFillColor(26,26,46);
    pdf.rect(0,pg.getHeight()-12,pg.getWidth(),12,"F");
    pdf.setTextColor(150,150,150); pdf.setFontSize(8);
    pdf.text(`${c.case_number} | oSecurity Ltd | CONFIDENTIAL`,14,pg.getHeight()-4);

    pdf.save(`${c.case_number}-${c.title.replace(/\s+/g,"-")}.pdf`);
  };

  const filtered = cases.filter(c => filter==="all" || c.status===filter);

  const stats = {
    open:         cases.filter(c=>c.status==="open").length,
    investigating:cases.filter(c=>c.status==="investigating").length,
    escalated:    cases.filter(c=>c.status==="escalated").length,
    closed:       cases.filter(c=>c.status==="closed").length,
  };

  return (
    <div style={{ padding:"0 24px 24px", color:"#1A1A2E" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0 16px" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800 }}>Case Management</div>
          <div style={{ fontSize:11, color:"#9B8F7A", marginTop:2 }}>
            {cases.length} cases · Link incidents · Evidence · PDF reports
          </div>
        </div>
        <button style={S.btn("#C9A84C","#1A1A2E","#C9A84C")} onClick={()=>setShowNew(true)}>+ New Case</button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[["Open",stats.open,"#2E7D52"],["Investigating",stats.investigating,"#C9A84C"],["Escalated",stats.escalated,"#C0392B"],["Closed",stats.closed,"#9B8F7A"]].map(([l,v,c])=>(
          <div key={l} style={{ ...S.card, marginBottom:0, borderLeft:`3px solid ${c}`, padding:"14px 16px", cursor:"pointer" }} onClick={()=>setFilter(l.toLowerCase())}>
            <div style={{ fontSize:26, fontWeight:800, color:c }}>{v}</div>
            <div style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", marginTop:3 }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns: selected ? "1fr 420px" : "1fr", gap:16 }}>
        {/* Cases list */}
        <div>
          {/* Filter */}
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            {["all","open","investigating","escalated","closed"].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{
                background:filter===f?"#1A1A2E":"#FFFFFF", color:filter===f?"#C9A84C":"#6B6055",
                border:"1px solid #E2DDD6", padding:"5px 14px", borderRadius:20,
                cursor:"pointer", fontSize:11, fontWeight:700, textTransform:"capitalize"
              }}>{f}</button>
            ))}
          </div>

          {filtered.length===0
            ? <div style={{ ...S.card, textAlign:"center", padding:48, color:"#9B8F7A" }}>No cases yet. Click + New Case to get started.</div>
            : filtered.map(c => {
              const sc = CASE_STATUSES.find(s=>s.id===c.status)||CASE_STATUSES[0];
              const isSelected = selected?.id===c.id;
              return (
                <div key={c.id} onClick={()=>setSelected(c)} style={{
                  ...S.card, cursor:"pointer",
                  borderLeft:`3px solid ${sc.color}`,
                  background: isSelected?"#FFFBF0":"#FFFFFF",
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:10, fontWeight:700, color:"#9B8F7A" }}>{c.case_number}</span>
                        <span style={{ background:sc.bg, color:sc.color, padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700 }}>{sc.label}</span>
                        <span style={{ background: c.priority==="high"?"#FEF2F2":c.priority==="medium"?"#FFF8E7":"#F0F2F8", color:c.priority==="high"?"#C0392B":c.priority==="medium"?"#C9A84C":"#6B6055", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700 }}>{(c.priority||"medium").toUpperCase()}</span>
                      </div>
                      <div style={{ fontSize:14, fontWeight:700, color:"#1A1A2E" }}>{c.title}</div>
                      {c.description && <div style={{ fontSize:12, color:"#6B6055", marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:400 }}>{c.description}</div>}
                      <div style={{ display:"flex", gap:12, marginTop:8 }}>
                        {c.assigned_name && <span style={{ fontSize:11, color:"#9B8F7A" }}>Officer: {c.assigned_name}</span>}
                        {(c.linked_incidents||[]).length>0 && <span style={{ fontSize:11, color:"#9B8F7A" }}>{c.linked_incidents.length} incident{c.linked_incidents.length>1?"s":""} linked</span>}
                        {(c.notes||[]).length>0 && <span style={{ fontSize:11, color:"#9B8F7A" }}>{c.notes.length} note{c.notes.length>1?"s":""}</span>}
                        <span style={{ fontSize:11, color:"#9B8F7A" }}>{fmtDate(c.created_at)}</span>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6, marginLeft:12 }}>
                      <button style={S.btn("#F4F2EE","#6B6055","#E2DDD6")} onClick={e=>{e.stopPropagation();exportPDF(c);}}>PDF</button>
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>

        {/* Case detail panel */}
        {selected && (
          <div style={{ ...S.card, position:"sticky", top:20, maxHeight:"80vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <div style={{ fontSize:11, color:"#9B8F7A", fontWeight:700 }}>{selected.case_number}</div>
                <div style={{ fontSize:15, fontWeight:800, color:"#1A1A2E" }}>{selected.title}</div>
              </div>
              <button onClick={()=>setSelected(null)} style={{ background:"none", border:"1px solid #E2DDD6", borderRadius:6, padding:"4px 10px", cursor:"pointer", color:"#9B8F7A", fontSize:12 }}>Close</button>
            </div>

            {/* Status control */}
            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>Status</label>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {CASE_STATUSES.map(s=>(
                  <button key={s.id} onClick={()=>updateStatus(selected.id,s.id)} style={{
                    background:selected.status===s.id?s.color:s.bg,
                    color:selected.status===s.id?"#fff":s.color,
                    border:`1px solid ${s.color}44`, padding:"5px 12px", borderRadius:20,
                    cursor:"pointer", fontSize:11, fontWeight:700
                  }}>{s.label}</button>
                ))}
              </div>
            </div>

            {/* Details */}
            {selected.description && (
              <div style={{ marginBottom:14 }}>
                <label style={S.lbl}>Description</label>
                <div style={{ fontSize:13, color:"#6B6055", lineHeight:1.5 }}>{selected.description}</div>
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={S.lbl}>Assigned Officer</label>
                <div style={{ fontSize:13, color:"#1A1A2E", fontWeight:600 }}>{selected.assigned_name||"Unassigned"}</div>
              </div>
              <div>
                <label style={S.lbl}>Priority</label>
                <div style={{ fontSize:13, color:"#1A1A2E", fontWeight:600, textTransform:"capitalize" }}>{selected.priority||"Medium"}</div>
              </div>
            </div>

            {/* Linked incidents */}
            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>Linked Incidents ({(selected.linked_incidents||[]).length})</label>
              {(selected.linked_incidents||[]).length===0
                ? <div style={{ fontSize:12, color:"#9B8F7A" }}>No incidents linked yet.</div>
                : incidents.filter(i=>(selected.linked_incidents||[]).includes(i.id)).map(inc=>(
                  <div key={inc.id} style={{ background:"#F8F7F4", border:"1px solid #E2DDD6", borderRadius:8, padding:"8px 12px", marginBottom:6 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#1A1A2E" }}>{inc.category} — {inc.siteName}</div>
                    <div style={{ fontSize:11, color:"#9B8F7A" }}>{inc.escalationLevel} · {inc.guardName} · {fmtDate(inc.createdAt)}</div>
                  </div>
                ))
              }
              {/* Link more incidents */}
              <select style={{ ...S.sel, marginTop:8, fontSize:12 }} onChange={async e=>{
                const id = e.target.value;
                if (!id || (selected.linked_incidents||[]).includes(id)) return;
                const updated = [...(selected.linked_incidents||[]), id];
                await updateDoc(doc(db,"cases",selected.id), { linked_incidents:updated, updated_at:serverTimestamp() });
                setSelected(s=>({...s,linked_incidents:updated}));
                e.target.value="";
              }}>
                <option value="">+ Link an incident...</option>
                {incidents.filter(i=>!(selected.linked_incidents||[]).includes(i.id)).map(i=>(
                  <option key={i.id} value={i.id}>{i.category} — {i.siteName} ({fmtDate(i.createdAt)})</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>Investigation Notes ({(selected.notes||[]).length})</label>
              {(selected.notes||[]).map((note,i)=>(
                <div key={i} style={{ background:"#F8F7F4", border:"1px solid #E2DDD6", borderRadius:8, padding:"10px 12px", marginBottom:6 }}>
                  <div style={{ fontSize:11, color:"#9B8F7A", marginBottom:4 }}>{note.author} · {new Date(note.created_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                  <div style={{ fontSize:13, color:"#1A1A2E", lineHeight:1.4 }}>{note.text}</div>
                </div>
              ))}
              <div style={{ display:"flex", gap:8, marginTop:8 }}>
                <input style={{ ...S.inp, flex:1 }} placeholder="Add investigation note..." value={newNote} onChange={e=>setNewNote(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addNote()}/>
                <button style={S.btn("#1A1A2E","#C9A84C","#1A1A2E")} onClick={addNote} disabled={addingNote}>Add</button>
              </div>
            </div>

            {/* Export */}
            <button style={{ ...S.btn("#C9A84C","#1A1A2E","#C9A84C"), width:"100%", padding:12 }} onClick={()=>exportPDF(selected)}>
              Export PDF Report
            </button>
          </div>
        )}
      </div>

      {/* New Case Modal */}
      {showNew && (
        <div style={{ position:"fixed", inset:0, background:"rgba(26,26,46,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#FFFFFF", borderRadius:14, padding:28, width:520, maxHeight:"85vh", overflowY:"auto", boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div style={{ fontSize:17, fontWeight:800, color:"#1A1A2E" }}>New Case</div>
              <button onClick={()=>setShowNew(false)} style={{ background:"none", border:"1px solid #E2DDD6", borderRadius:6, padding:"4px 10px", cursor:"pointer", color:"#9B8F7A" }}>Close</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.lbl}>Case Title *</label>
                <input style={S.inp} placeholder="e.g. Repeated theft at Paddington" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.lbl}>Description</label>
                <textarea style={{ ...S.inp, minHeight:70, resize:"vertical" }} placeholder="Case summary..." value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
              </div>
              <div>
                <label style={S.lbl}>Assigned Officer</label>
                <select style={S.sel} value={form.assigned_to} onChange={e=>setForm(f=>({...f,assigned_to:e.target.value}))}>
                  <option value="">Select officer...</option>
                  {officers.map(o=><option key={o.uid} value={o.uid}>{o.name||o.email}</option>)}
                </select>
              </div>
              <div>
                <label style={S.lbl}>Priority</label>
                <select style={S.sel} value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.lbl}>Link Incidents</label>
                <select style={S.sel} onChange={e=>{
                  const id=e.target.value;
                  if(id&&!form.linked_incidents.includes(id)) setForm(f=>({...f,linked_incidents:[...f.linked_incidents,id]}));
                  e.target.value="";
                }}>
                  <option value="">Select incident to link...</option>
                  {incidents.filter(i=>!form.linked_incidents.includes(i.id)).map(i=>(
                    <option key={i.id} value={i.id}>{i.category} — {i.siteName} ({fmtDate(i.createdAt)})</option>
                  ))}
                </select>
                {form.linked_incidents.length>0&&(
                  <div style={{ marginTop:8, display:"flex", flexWrap:"wrap", gap:6 }}>
                    {form.linked_incidents.map(id=>{
                      const inc=incidents.find(i=>i.id===id);
                      return inc?(
                        <span key={id} style={{ background:"#F0F2F8", color:"#1A1A2E", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
                          {inc.category}
                          <button onClick={()=>setForm(f=>({...f,linked_incidents:f.linked_incidents.filter(i=>i!==id)}))} style={{ background:"none", border:"none", cursor:"pointer", color:"#9B8F7A", fontSize:14, lineHeight:1 }}>×</button>
                        </span>
                      ):null;
                    })}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display:"flex", gap:10, marginTop:22 }}>
              <button style={{ ...S.btn("#FFFFFF","#6B6055","#E2DDD6"), flex:1 }} onClick={()=>setShowNew(false)}>Cancel</button>
              <button style={{ ...S.btn("#C9A84C","#1A1A2E","#C9A84C"), flex:2 }} onClick={createCase}>Create Case</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
